// src/SoilUpload.js
import React, { useEffect, useState } from "react";
import { createMRVSample, listMRVSamples, csvURLForMRV, apiBase } from "./api";
import { useAuth } from "./AuthContext";
import Camera from "./components/Camera";

export default function SoilUpload({ compact = false }) {
  const auth = useAuth();
  const me = auth?.me?.() || null;
  const myEmail = (me?.email || "").trim();

  // use strings for all inputs (no coercion)
  const [form, setForm] = useState({
    plot_id: "",
    sample_date: "", // YYYY-MM-DD
    sample_depth_cm: "",
    soil_organic_carbon_pct: "",
    bulk_density_g_cm3: "",
    ph: "",
    moisture_pct: "",
    lab_id: "",
    method_ref: "core",
  });

  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [lastSent, setLastSent] = useState(null);
  const [lastEcho, setLastEcho] = useState(null);
  const [err, setErr] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    console.log('📝 Input changed:', name, '=', value); // DEBUG
    // keep value EXACTLY as typed
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handlePhotoCapture = (photoData) => {
    setPhoto(photoData);
    console.log('📸 Photo captured:', photoData.timestamp);
  };

  async function refreshList() {
    try {
      setErr("");
      const r = await listMRVSamples({ email: myEmail });
      setRows(r?.rows || []);
    } catch (e) {
      setErr(e.message || "Failed to load samples");
    }
  }

  useEffect(() => {
    refreshList(); // on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail]);

  async function submit(e) {
    e.preventDefault();
    
    // DEBUG: Log form state
    console.log('🔍 === SUBMIT CLICKED ===');
    console.log('🔍 Form state:', form);
    console.log('🔍 Photo state:', photo);
    
    setSubmitting(true);
    setErr("");
    setLastSent(null);
    setLastEcho(null);

        // Build payload with ONLY present keys; all are strings
    const payload = {};
    for (const k of Object.keys(form)) {
      if (form[k] !== "") {
        // Convert date format if it's sample_date
        if (k === 'sample_date' && form[k]) {
          // Convert MM/DD/YYYY or DD/MM/YYYY to YYYY-MM-DD
          const dateValue = form[k];
          if (dateValue.includes('/')) {
            const parts = dateValue.split('/');
            // Assume MM/DD/YYYY format
            const [month, day, year] = parts;
            payload[k] = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          } else {
            payload[k] = dateValue; // Already in correct format
          }
        } else {
          payload[k] = form[k];
        }
      }
    }

    // DEBUG: Log payload being built
    console.log('🔍 Payload after filtering empty strings:', payload);

    // Add photo info if captured
    if (photo) {
      payload.photo_timestamp = photo.timestamp;
      payload.has_photo = true;
      console.log('🔍 Photo data added to payload');
    }

    console.log('🔍 Final payload to send:', payload);

    try {
      setLastSent({
        email: myEmail,
        apiBase: apiBase(),
        payload,
        photoAttached: !!photo
      });

      console.log('🔍 Calling createMRVSample with email:', myEmail);
      const res = await createMRVSample({ email: myEmail, data: payload });
      console.log('✅ Success! Response:', res);
      
      setLastEcho(res?.record || res);
      await refreshList();
      
      // Clear form and photo after successful submission
      setForm({
        plot_id: "",
        sample_date: "",
        sample_depth_cm: "",
        soil_organic_carbon_pct: "",
        bulk_density_g_cm3: "",
        ph: "",
        moisture_pct: "",
        lab_id: "",
        method_ref: "core",
      });
      setPhoto(null);
      console.log('✅ Form and photo cleared');
    } catch (e) {
      console.error('❌ Submit error:', e);
      setErr(e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  const csvHref = csvURLForMRV({ email: myEmail });

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, background: "#fff" }}>
      <div style={{ marginBottom: 8, fontSize: 14, color: "#555" }}>
        API: <code>{apiBase()}</code>
      </div>

      <h3 style={{ marginTop: 0 }}>Soil Samples (MRV)</h3>

      {err && (
        <div style={{ background: "#fee2e2", color: "#991b1b", padding: 10, borderRadius: 8, marginBottom: 12 }}>
          {err}
        </div>
      )}

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        
      {/* Camera Section */}
      <div>
        <Camera 
          onPhotoCapture={handlePhotoCapture}
          label="📸 Soil Sample Photo"
        />
      </div>

        {/* Form Fields */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
          <Text name="plot_id" label="Plot ID" value={form.plot_id} onChange={onChange} />
          <Text name="sample_date" label="Sample Date (YYYY-MM-DD)" value={form.sample_date} onChange={onChange} />
          <Text name="sample_depth_cm" label="Sample Depth (cm)" value={form.sample_depth_cm} onChange={onChange} />
          <Text name="soil_organic_carbon_pct" label="SOC (%)" value={form.soil_organic_carbon_pct} onChange={onChange} />
          <Text name="bulk_density_g_cm3" label="Bulk Density (g/cm³)" value={form.bulk_density_g_cm3} onChange={onChange} />
          <Text name="ph" label="pH" value={form.ph} onChange={onChange} />
          <Text name="moisture_pct" label="Moisture (%)" value={form.moisture_pct} onChange={onChange} />
          <Text name="lab_id" label="Lab ID" value={form.lab_id} onChange={onChange} />
          <Text name="method_ref" label="Method Ref" value={form.method_ref} onChange={onChange} />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button 
            type="submit" 
            disabled={submitting}
            style={{
              padding: '12px 24px',
              background: photo ? '#4ade80' : '#6b7280',
              color: photo ? '#0f2419' : 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {submitting ? "Submitting…" : photo ? "📸 Submit Sample with Photo" : "Submit Sample"}
          </button>
          <a href={csvHref} target="_blank" rel="noreferrer" style={{ lineHeight: "30px" }}>
            Download my CSV
          </a>
          <button type="button" onClick={refreshList}>Refresh list</button>
        </div>
      </form>

      {/* Diagnostics: what we sent vs what API echoed */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
        <Box title="Last payload SENT">
          {lastSent ? <pre style={pre}>{JSON.stringify(lastSent, null, 2)}</pre> : <span>—</span>}
        </Box>
        <Box title="Last record ECHOED by API">
          {lastEcho ? <pre style={pre}>{JSON.stringify(lastEcho, null, 2)}</pre> : <span>—</span>}
        </Box>
      </div>

      {/* Recent rows */}
      {!compact && (
        <div style={{ marginTop: 16 }}>
          <h4>My recent samples</h4>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  {["sample_id","plot_id","sample_date","sample_depth_cm","soil_organic_carbon_pct","bulk_density_g_cm3","ph","moisture_pct","created_at"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((r) => (
                  <tr key={r.sample_id}>
                    <td style={td}>{r.sample_id}</td>
                    <td style={td}>{r.plot_id}</td>
                    <td style={td}>{r.sample_date}</td>
                    <td style={td}>{String(r.sample_depth_cm ?? "")}</td>
                    <td style={td}>{String(r.soil_organic_carbon_pct ?? "")}</td>
                    <td style={td}>{String(r.bulk_density_g_cm3 ?? "")}</td>
                    <td style={td}>{String(r.ph ?? "")}</td>
                    <td style={td}>{String(r.moisture_pct ?? "")}</td>
                    <td style={td}>{r.created_at}</td>
                  </tr>
                ))}
                {(!rows || rows.length === 0) && (
                  <tr><td style={td} colSpan={9}>No samples yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Text({ label, name, value, onChange }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
      <input
        name={name}
        value={value}
        onChange={onChange}
        style={{ width: "100%", padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
      />
    </label>
  );
}

function Box({ title, children }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

const pre = { margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 };
const th = { borderBottom: "1px solid #e5e7eb", padding: "8px 6px", textAlign: "left", fontSize: 12, color: "#6b7280" };
const td = { borderBottom: "1px solid #f1f5f9", padding: "8px 6px", fontSize: 13 };